import React, { useState } from 'react'
import { Button, Toast } from 'components'
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { bulkUpdateFleets } from 'api'
import { BulkOperation, IRecipientCandidate, IValidationTypeData } from 'interfaces/IFleetMonitoring'
import strings from 'strings'
import RecipientsInput from '../../recipientsInput'

const FM = strings.FLEET_MONITORING

interface IBulkRecipientsModalProps {
  open: boolean
  carrierIds: number[]
  validationTypes: IValidationTypeData[]
  candidates: IRecipientCandidate[]
  onClose: () => void
  onApplied: () => void
}

type RecipientMode = 'setRecipients' | 'appendRecipients' | 'clearRecipients'

const BulkRecipientsModal: React.FunctionComponent<IBulkRecipientsModalProps> = ({
  open,
  carrierIds,
  validationTypes,
  candidates,
  onClose,
  onApplied,
}) => {
  const [mode, setMode] = useState<RecipientMode>('setRecipients')
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([])
  const [scopeAll, setScopeAll] = useState(true)
  const [scopedCodes, setScopedCodes] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)

  React.useEffect(() => {
    if (open) {
      setMode('setRecipients')
      setSelectedUserIds([])
      setScopeAll(true)
      setScopedCodes([])
    }
  }, [open])

  const needsRecipients = mode !== 'clearRecipients'
  const canApply =
    (!needsRecipients || selectedUserIds.length > 0) && (scopeAll || scopedCodes.length > 0)

  const toggleCode = (code: string) => {
    setScopedCodes(prev => (prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]))
  }

  const handleApply = async () => {
    setSubmitting(true)
    try {
      await bulkUpdateFleets({
        carrierIds,
        operation: mode as BulkOperation,
        payload: {
          ...(needsRecipients && { recipients: selectedUserIds }),
          ...(!scopeAll && { validationTypeCodes: scopedCodes }),
        },
      })
      Toast({
        type: 'success',
        subTitle: FM.TOASTS.RECIPIENTS_UPDATED(carrierIds.length),
      })
      onApplied()
      onClose()
    } catch (e: any) {
      Toast({ type: 'error', subTitle: e?.message || FM.ERRORS.UPDATE_RECIPIENTS_FAILED })
    } finally {
      setSubmitting(false)
    }
  }

  const count = carrierIds.length

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {FM.BULK_RECIPIENTS.TITLE}
        <IconButton size="small" onClick={onClose} aria-label="close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {FM.BULK_RECIPIENTS.APPLYING_TO(count)}
        </Typography>

        <ToggleButtonGroup
          value={mode}
          exclusive
          fullWidth
          size="small"
          onChange={(_e, v) => v && setMode(v)}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="setRecipients">{FM.BULK_RECIPIENTS.REPLACE}</ToggleButton>
          <ToggleButton value="appendRecipients">{FM.BULK_RECIPIENTS.ADD_TO_EXISTING}</ToggleButton>
          <ToggleButton value="clearRecipients">{FM.BULK_RECIPIENTS.CLEAR_ALL}</ToggleButton>
        </ToggleButtonGroup>

        {needsRecipients ? (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              {FM.BULK_RECIPIENTS.EMAILS_LABEL}
            </Typography>
            <RecipientsInput
              value={selectedUserIds}
              onChange={setSelectedUserIds}
              candidates={candidates}
            />
          </>
        ) : (
          <Typography variant="body2" sx={{ p: 2, background: '#fff8e1', borderRadius: 1 }}>
            {FM.BULK_RECIPIENTS.CLEAR_WARNING}
          </Typography>
        )}

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 2, mb: 0.5 }}
        >
          {FM.BULK_RECIPIENTS.APPLY_TO_LABEL}
        </Typography>
        <FormControlLabel
          control={<Checkbox checked={scopeAll} onChange={e => setScopeAll(e.target.checked)} />}
          label={FM.BULK_RECIPIENTS.APPLY_TO_ALL}
        />
        {!scopeAll && (
          <div style={{ display: 'flex', flexDirection: 'column', paddingLeft: 8 }}>
            {validationTypes.map(vt => (
              <FormControlLabel
                key={vt.code}
                control={
                  <Checkbox
                    size="small"
                    checked={scopedCodes.includes(vt.code)}
                    onChange={() => toggleCode(vt.code)}
                  />
                }
                label={vt.name}
              />
            ))}
          </div>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button label={FM.COMMON.CANCEL} variant="text" onClick={onClose} disabled={submitting} />
        <Button
          label={FM.BULK_RECIPIENTS.APPLY_TO(count)}
          variant="contained"
          onClick={handleApply}
          disabled={submitting || !canApply}
        />
      </DialogActions>
    </Dialog>
  )
}

export default BulkRecipientsModal
